import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import * as moment from 'moment-timezone';
import { Subscription, interval } from 'rxjs';
import { Advertiser } from '../core/models/advertiser';
import { Brand } from '../core/models/brand';
import { User } from '../core/models/user';
import { ApiHelper } from '../core/services/apiHelper';
import { AuthService } from '../core/services/auth.service';
import { AdvertiserDao } from '../core/services/dao/advertiserDao';
import { BrandDao } from '../core/services/dao/brandDao';
import { UserDao } from '../core/services/dao/userDao';
import { environment } from '../environments/environment';
import { ErrorDialogService } from './shared/errorDialog/errorDialogService';
import { TermsService } from './shared/terms/terms-service';
import { LookerDashboardMetadataDAO } from '../core/services/dao/lookerDashboardMetadataDAO';
import { LookerDashboardMetadata } from '../core/models/lookerDashboardMetadata';
import { unsubscribeFromAll } from '../core/rx-helper';
import { sortAlphabetical } from './shared/helper-functions/sorting-helpers';
import { ProfileDao } from '../core/services/dao/avs/profileDao';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';

interface IsSelectedStore {
  onboarding: boolean;
  user_settings: boolean;
  help: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  isLoggedIn = false;
  isReady = false;
  isErrorDialogActive = false;
  isGreenScreen = false;

  busy: Subscription;
  user: User;
  isAdmin = false;
  impersonating = false;
  isAuthUser = false;
  isAgency = false;
  isAdvManager = false;
  isChangeManagement = false;
  isExceptionsUser = false;
  isAdCodeManager = false;
  isDetectionReportsUser = false;

  settingsExpanded = false;
  accountSettingsExpanded = false;
  isBilled = false;

  isAdminRoute = false;
  isImpersonateRoute = false;
  isAdvSettingsRoute = false;
  demoDashboard = false;

  @ViewChild('sidemenu') sidemenu: MatSidenav;

  brands: Array<Brand> = [];
  selectedBrandId = '';
  lastKnownBrandId: string = null;

  advertisers: Array<Advertiser> = [];
  selectedAdvId = '';
  selectedAdvName = '';

  isAdminEnabled: boolean;

  currentYear = '2018';
  environment = '';

  isSelectedStore: IsSelectedStore = { onboarding: false, user_settings: false, help: false };

  unreadAlertCount: number;

  disabledBrands: Array<Brand> = [];
  enabledBrands: Array<Brand> = [];

  lookerSidenavGroupings: any = {};
  subs: Subscription[] = [];
  lookerMetadataLoaded = false;

  constructor(
    private apiHelper: ApiHelper,
    private authenticationService: AuthService,
    private router: Router,
    private userDao: UserDao,
    public errorDialogService: ErrorDialogService,
    private termsService: TermsService,
    private brandDao: BrandDao,
    private advertiserDao: AdvertiserDao,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private lookerDashboardMetadataDAO: LookerDashboardMetadataDAO,
    private profileDao: ProfileDao,
    private location: Location

  ) {
    this.environment = environment.envName;
    this.apiHelper.setEnvironment(environment.envName);
    let that = this;
    let brandInit = false;
    let advInit = false;
    if (window.location.href.includes('demo-dashboard')) {
      this.demoDashboard = true;
    }


    this.router.events.subscribe(event => {
      this.getLookerDashboardMetadata()
    })

    this.currentYear = moment.tz('UTC').format('YYYY');

    this.advertiserDao.adBlockerCheck().subscribe(
      res => {},
      err => {}
    );

    const brandSub = this.brandDao.brandChange$.subscribe(brand => {
      this.selectedBrandId = brand ? brand.id : '';
      if (brand && brand.name && brandInit) {
        this.showBrandChangeSnack(brand.name);
      }

      if (brand) {
        brandInit = true;
      }

      if (this.lastKnownBrandId && this.selectedBrandId !== this.lastKnownBrandId && !this.isAdminRoute) {
        window.location.reload();
      }
      this.lastKnownBrandId = this.selectedBrandId;
    });
    this.subs.push(brandSub)

    const advSub = this.advertiserDao.advertiserChange$.subscribe(advertiser => {
      let next = this.route.snapshot.queryParams.next;
      this.user = this.userDao.getCurrentUser();
      this.setUserTypes();

      if (advertiser) {
        this.selectedAdvId = advertiser.id;
        this.isAdminEnabled = advertiser.isAdminEnabled;
        this.enabledBrands = [];
        this.disabledBrands = [];


        if (this.user.fullAccessAdvertisers?.includes(this.selectedAdvId)) {
          const allBrandsSub = this.brandDao.findByAdvertiserId(this.selectedAdvId)
            .subscribe(brands => {
              this.brands = brands;
              this.initBrands(true);
            })
          this.subs.push(allBrandsSub)
        } else {
          this.brands = advertiser.brands;
          this.initBrands();
        }

        if (this.isAdmin) {
          this.impersonating = true;
          this.selectedAdvName = advertiser.name;
          this.isAgency = !!advertiser.agency;
        }

        if (advInit) {
          let brandIds = advertiser.brands.map(i => i.id);
          if (!brandIds.includes(this.selectedBrandId)) {
            this.brandDao.setCurrentBrand('');
          }
        }
        if (this.brands.length === 1) {
          this.brandDao.setCurrentBrand(this.brands[0].id);
          brandInit = false;
        }

        if (window.location.pathname === '/login/' + this.selectedAdvId) {
          this.router.navigate(['manage/ad-codes']).catch(err => console.error(err));
        } else {
          if (!window.location.pathname.includes('/manage')) {
            if (next) {
              this.router.navigate([next]).catch(err => console.error(err));
            } else if (this.isAdvManager && !this.user.bvsOnly) {
              this.router.navigate(['manage', 'ad-codes']);
            } else if (this.isAdvManager && this.user.bvsOnly) {
              this.router.navigate(['manage', 'veil-encoder']);
            } else {
              this.router.navigate(['manage']);
            }
            advInit = true;
          }
        }
      } else {
        this.impersonating = false;
        this.selectedAdvName = '';
      }
    });
    this.subs.push(advSub)

    const isAdminEnabledSub = this.advertiserDao.isAdminEnabledChange$.subscribe(res => {
      this.isAdminEnabled = res;
    });
    this.subs.push(isAdminEnabledSub)

    const authSub = this.authenticationService.getSessionAuthenticatedSubject().subscribe(loggedIn => {
      this.user = this.userDao.getCurrentUser();
      this.isLoggedIn = loggedIn;
      this.isAdmin = !!this.user.isAdmin;

      if (this.isLoggedIn) {
        this.setUserTypes();

        let advId = window.localStorage.getItem('advocado_advertiser');
        let brandId = window.localStorage.getItem('advocado_brand');

        if (this.isAdmin) {
          if (advId) {
            this.selectedBrandId = brandId || '';
            this.selectBrand();
            this.selectedAdvId = advId;
            this.selectAdvertiser();
          }

        } else {
          this.advertisers = this.user.advertisers;
          let adv = this.advertisers?.find(ad => ad.id === advId);
          if (adv) {
            this.selectedAdvId = advId;
          } else if (this.user.advertisers.length === 1) {
            this.selectedAdvId = this.advertisers[0].id;
            adv = this.advertisers[0];
          }

          if (this.selectedAdvId) {
            if (adv.brands) {
              this.brands = adv.brands;
              let brand = this.brands.find(b => b.id === brandId);
              if (brandId && brand) {
                this.selectedBrandId = brandId;
                this.selectBrand();
              }
            }

            this.selectAdvertiser();
          } else {
            this.router.navigate(['pick-advertiser']);
          }
        }
      } else {
        this.isAdmin = false;
        this.impersonating = false;
        this.isAuthUser = false;
      }

      if (this.isLoggedIn && this.isReady) {
        this.checkTerms();
      } else if (!this.isReady) {
        setTimeout(() => {
          this.checkTerms();
        }, 1500);
      }
    });
    this.subs.push(authSub);

    const busySub = this.apiHelper.busyChange$.subscribe(busy => {
      this.busy = busy.subscribe();
      this.user = this.userDao.getCurrentUser();
      if (this.user) {
        this.isAdmin = !!this.user.isAdmin;
      }
    });
    this.subs.push(busySub)
    const apiErrorSub = this.apiHelper.apiErrorChange$.subscribe(apiError => {
      console.log(apiError);
      if (!that.isErrorDialogActive) {
        that.isErrorDialogActive = true;
        this.errorDialogService.open(apiError.title, apiError.message).subscribe(result => {
          console.log(result);
          that.isErrorDialogActive = false;
        });
      }
    });
    this.subs.push(apiErrorSub);
  }

  initBrands(isFullAccess?: boolean): void {
    this.brands.forEach(brand => {
      if (brand.format?.profileId) {
        let profileId = brand.format.profileId;
        const profileSub = this.profileDao.find({profileId: profileId}).subscribe(
          res => {
            brand['profileName'] = res[0].profileName
            this.sortBrands(brand, isFullAccess)
          }
        );
        this.subs.push(profileSub)
      } else {
        brand['profileName'] = 'No Profile';
        this.sortBrands(brand, isFullAccess);
      }
    });
  }


  sortBrands(brand: Brand, isFullAccess?: boolean) {
    if (brand.isBilledInApp === true) {
      this.isBilled = true;
    }

    if ((this.isAdmin || this.user.brands.includes(brand._id) || isFullAccess) && brand.isUserEnabled) {
      this.enabledBrands.push(brand);
    } else if(!brand.isUserEnabled) {
      this.disabledBrands.push(brand);
    }

    this.enabledBrands.sort((a, b) => sortAlphabetical(a,b, 'name'));

    this.disabledBrands.sort((a, b) => sortAlphabetical(a,b, 'name'));
  }

  checkTerms() {
    try {
      if (!this.user.termsAccepted) {
        this.termsService.display(true).subscribe(
          accepted => {
            if (accepted) {
              this.user.termsAccepted = true;

              setTimeout(() => {
                this.userDao.acceptTerms().subscribe(
                  res => {},
                  err => console.error(err)
                );
              }, 0);
            } else {
              setTimeout(() => {
                this.checkTerms();
              }, 1000);
            }
          },
          err => {
            console.error(err);
          }
        );
      }
    } catch (err) {
      console.error(err);
    }
  }

  ngOnInit() {

    this.getLookerDashboardMetadata();

    if (window.location.pathname.includes('manage') && !window.location.pathname.includes('manage/schedule/review')) {
      this.authenticationService.checkSession().subscribe(
        () => {
          this.user = this.userDao.getCurrentUser();
          this.setUserTypes();
          this.isReady = true;
        },
        () => {
          this.router.navigate(['login']).catch(err => console.error(err));
        }
      );
    }

    const routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.userDao.getCurrentUser();
        this.isGreenScreen =
          event.urlAfterRedirects.includes('login') ||
          event.urlAfterRedirects.includes('select-app') ||
          event.urlAfterRedirects.includes('home');
        //hide the global selectors if the admin is on the admin route
        this.isAdminRoute = event.url.includes('admin');
        this.isImpersonateRoute = event.url.includes('impersonate');
        this.isAdvSettingsRoute = event.url.includes('advertiser-settings');
      }

      let content = document.querySelector('.mat-sidenav-content') || window;
      if (event instanceof NavigationEnd) {
        setTimeout(function() {
          content.scrollTo(0, 0);
        }, 0);
        setTimeout(() => {
          this.initSelectedStore();
        }, 0);
      }
    });
    this.subs.push(routerSub);
    this.initSelectedStore();
    setTimeout(() => {
      if (window.location.pathname.includes('manage/schedule/review')) {
        this.sidemenu?.close()
      }
    }, 1000)
  }

  getLookerDashboardMetadata(): void {
    if (!this.isPublicRoute() && !this.lookerMetadataLoaded) {
      const lookerMetaSub = this.lookerDashboardMetadataDAO.load().subscribe(metadata => {

        metadata.forEach((meta: LookerDashboardMetadata) => {
          if (!this.lookerSidenavGroupings[meta.sidenav_group]) {
            this.lookerSidenavGroupings[meta.sidenav_group] = [];
          }

          const metaIdx = this.lookerSidenavGroupings[meta.sidenav_group].findIndex(dash => {
            return dash._id === meta['_id']
          })

          if (metaIdx === -1) {
            this.lookerSidenavGroupings[meta.sidenav_group].push(meta)
          }

        })
        this.lookerMetadataLoaded = true;
      });
      this.subs.push(lookerMetaSub)
    }
  }

  isPublicRoute(): boolean {
    return window.location.href.includes('/login') ||
      window.location.href.includes('/create-account') ||
      window.location.href.includes('/forgot-password') ||
      window.location.href.includes('/reset-password') ||
      window.location.pathname === '/'
  }

  initSelectedStore() {
    this.isSelectedStore.onboarding = window.location.pathname.includes('manage/onboarding');
    this.isSelectedStore.user_settings = window.location.pathname.includes('manage/user-profile');
    this.isSelectedStore.help = window.location.pathname.includes('support');
  }

  logout() {
    this.authenticationService.logout().subscribe(
      () => {
        this.advertisers = [];
        this.brands = [];
        this.selectedAdvId = '';
        this.selectedBrandId = '';
        this.isAdmin = false;
        this.impersonating = false;
        this.isAuthUser = false;
        this.advertiserDao.setCurrentAdvertiser(null);
        this.router.navigate(['login']).catch(err => console.error(err));
        this.snackBar.open('You have signed out of VEIL', 'Dismiss', {
          duration: 5000,
          verticalPosition: 'top'
        });
      },
      () => {
        console.log('logout failed');
      }
    );
  }

  goToProfile() {
    this.redirectTo('manage/user-profile');
  }

  goToBvsEncode() {
    this.redirectTo('manage/veil-encoder');
  }

  redirectTo(uri: string) {
    this.router.navigateByUrl('/manage', { skipLocationChange: true }).then(() => this.router.navigate([uri]));
  }


  setActiveNav(navLink): boolean {
    const currentPath = this.location.path();
    return navLink === currentPath;
  }

  ngOnDestroy() {
    // prevent memory leak when component destroyed
    this.busy.unsubscribe();
    unsubscribeFromAll(this.subs)
  }

  setUserTypes() {
    this.user.roles.forEach(role => {

      switch (role.type) {
        case 'Authenticated':
          this.isAuthUser = true;
          break;
        case 'AdvertiserManager':
          this.isAdvManager = true;
          break;
        case 'ChangeManagement':
          this.isChangeManagement = true;
          break;
        case 'Exceptions':
          this.isExceptionsUser = true;
          break;
        case 'AdCodeMetadataManager':
          this.isAdCodeManager = true;
          break;
        case 'DetectionReports':
          this.isDetectionReportsUser = true;
          break;
      }

    });
  }

  isAdminImpersonating() {
    return this.isAdmin && this.impersonating;
  }

  canAccessMenuItem() {
    return !this.isAdmin || this.impersonating;
  }

  canAccessAuthItem() {
    return this.isAuthUser || this.isAdmin;
  }

  canAccessDetectionReports() {
    return this.isDetectionReportsUser || this.isAdCodeManager || this.isAdvManager || this.isAdminImpersonating();
  }

  canAccessAdvManagerItem() {
    return this.isAdvManager || this.isAdminImpersonating();
  }

  canAccessChangeManagementItem() {
    return this.isChangeManagement || this.isAdminImpersonating();
  }

  isAdminOrAdvManager() {
    return this.isAdvManager || this.isAdmin;
  }

  canAccessExceptionsItem() {
    return this.isExceptionsUser || this.isAdminImpersonating();
  }

  isOver() {
    return window.matchMedia(`(max-width: 960px)`).matches;
  }

  menuMouseOver() {
    if (window.matchMedia(`(min-width: 960px)`).matches) {
      this.sidemenu.mode = 'over';
    }
  }

  menuMouseOut() {
    if (window.matchMedia(`(min-width: 960px)`).matches) {
      this.sidemenu.mode = 'side';
    }
  }

  toggle() {
    this.sidemenu.toggle();
  }

  selectBrand() {
    this.brandDao.setCurrentBrand(this.selectedBrandId);
  }

  selectAdvertiser() {
    this.advertiserDao.setCurrentAdvertiser(this.selectedAdvId);
  }

  toggleSettings() {
    this.settingsExpanded = !this.settingsExpanded;
  }

  toggleAccountSettings() {
    this.accountSettingsExpanded = !this.accountSettingsExpanded;
  }

  private showBrandChangeSnack(brandName: string) {
    this.snackBar.open(`Brand change: ${brandName} selected`, 'OK', {
      duration: 5000,
      verticalPosition: 'top'
    })
  }

  canAccessLookerItem(sidenav_group: any) {
    if (sidenav_group === 'Dashboards' || sidenav_group === 'Channel Monitoring') {
      return this.canAccessDetectionReports()
    } else if (sidenav_group === 'In-Flight') {
      return this.canAccessExceptionsItem();
    }
    return false;
  }

  canAccessAdCodes() {
    return this.isAdvManager || this.isAdCodeManager || this.isAdminImpersonating()
  }
}
